import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icons } from '@ohif/ui-next';
import classNames from 'classnames';
import { Menu, PanelLeft } from 'lucide-react';
import { authService } from '../../auth/authService';
import { getUserRoleDisplayName } from '../../utils/roleUtils';
import { useOptionalResponsiveLayout } from '../../context/ResponsiveLayoutContext';
import TatNotificationBell from '../notifications/TatNotificationBell';
import './PageHeader.css';

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface PageHeaderProps {
  title: string;
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  onBack?: () => void;
  /** Legacy breadcrumbs (`path` used with react-router `Link`) */
  breadcrumbs?: BreadcrumbItem[];
  notificationCount?: number;
  /** Light bar for Imaging Worklist */
  variant?: 'default' | 'light';
  /** Breadcrumbs for worklist (`to` used with `Link`) */
  breadcrumbItems?: { label: string; to?: string }[];
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  primaryAction,
  onBack,
  breadcrumbs = [],
  notificationCount = 0,
  variant = 'default',
  breadcrumbItems,
}) => {
  const navigate = useNavigate();
  const layout = useOptionalResponsiveLayout();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const showNavMenuButton = layout ? layout.isDrawerMode : false;
  const showAnalyticsMenuButton = Boolean(layout?.isDrawerMode && layout.showAnalyticsSubSidebar);

  const user = authService.getUser();
  const displayName = user?.username || user?.name?.split(' ')[0] || user?.given_name || 'User';
  const userRoleDisplay = getUserRoleDisplayName(user);
  const userInitials = (user?.name || user?.given_name || displayName || 'U')
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const breadcrumbTrail = React.useMemo(() => {
    if (breadcrumbItems?.length) {
      return breadcrumbItems.map(b => ({ label: b.label, path: b.to }));
    }
    return breadcrumbs.map(b => ({ label: b.label, path: b.path }));
  }, [breadcrumbItems, breadcrumbs]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handleLogout = async () => {
    await authService.logout({ scope: 'ris' });
    navigate('/login');
  };

  return (
    <div className={classNames('page-header', variant === 'light' && 'page-header--light')}>
      <div className="page-header-left">
        {showNavMenuButton ? (
          <button
            type="button"
            onClick={layout?.toggleNav}
            className="page-header-menu-btn"
            aria-label="Open navigation menu"
            aria-expanded={layout?.isNavOpen}
          >
            <Menu
              size={20}
              strokeWidth={2}
            />
          </button>
        ) : null}

        {showAnalyticsMenuButton ? (
          <button
            type="button"
            onClick={layout?.toggleAnalyticsNav}
            className="page-header-analytics-menu-btn"
            aria-label="Open analytics sections"
            aria-expanded={layout?.isAnalyticsNavOpen}
          >
            <PanelLeft
              size={18}
              strokeWidth={2}
            />
            <span className="page-header-analytics-menu-btn-label">Sections</span>
          </button>
        ) : null}

        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="page-header-back-btn"
            aria-label="Go back"
          >
            <Icons.ChevronLeft className="page-header-chevron-icon" />
          </button>
        )}

        {breadcrumbTrail.length > 0 && (
          <nav
            aria-label="Breadcrumb"
            className="page-header-breadcrumb-nav"
          >
            {breadcrumbTrail.map((item, i) => (
              <React.Fragment key={`${item.label}-${i}`}>
                {i > 0 && (
                  <span
                    className="page-header-breadcrumb-sep"
                    aria-hidden
                  >
                    {'>'}
                  </span>
                )}
                {item.path ? (
                  <Link
                    to={item.path}
                    className="page-header-breadcrumb-link"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span
                    className={classNames(
                      'page-header-breadcrumb-current',
                      Boolean(breadcrumbItems?.length) &&
                        i === breadcrumbTrail.length - 1 &&
                        'page-header-breadcrumb-current--emphasis'
                    )}
                  >
                    {item.label}
                  </span>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}

        {title && breadcrumbTrail.length > 0 ? (
          <span className="page-header-title-sep">|</span>
        ) : null}
        {title ? <h1 className="page-header-title">{title}</h1> : null}
      </div>

      <div className="page-header-right">
        {primaryAction && (
          <button
            type="button"
            onClick={primaryAction.onClick}
            className="page-header-primary-btn btn btn-primary"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M8 3.33337V12.6667M3.33334 8H12.6667"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="page-header-primary-btn-label">{primaryAction.label}</span>
          </button>
        )}

        <TatNotificationBell />

        <div className="page-header-profile-wrap">
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="page-header-profile-btn page-header-profile-btn-circle"
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
            aria-label="Profile menu"
          >
            <div className="page-header-avatar">{userInitials}</div>
          </button>

          {isDropdownOpen && (
            <div
              ref={dropdownRef}
              className="page-header-dropdown"
            >
              <div className="page-header-dropdown-user">
                <div className="page-header-dropdown-avatar">{userInitials}</div>
                <div className="page-header-dropdown-user-details">
                  <div className="page-header-dropdown-user-name">Hi {displayName}</div>
                  <div className="page-header-dropdown-user-role">{userRoleDisplay}</div>
                </div>
              </div>

              <button
                type="button"
                className="page-header-dropdown-item"
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate('/profile');
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden
                >
                  <path
                    d="M8 8C10.2091 8 12 6.20914 12 4C12 1.79086 10.2091 0 8 0C5.79086 0 4 1.79086 4 4C4 6.20914 5.79086 8 8 8Z"
                    fill="currentColor"
                  />
                  <path
                    d="M8 10C4.68629 10 2 12.6863 2 16H14C14 12.6863 11.3137 10 8 10Z"
                    fill="currentColor"
                  />
                </svg>
                <span>View Profile</span>
              </button>

              <button
                type="button"
                className="page-header-dropdown-item page-header-dropdown-item-logout"
                onClick={handleLogout}
              >
                <Icons.Logout className="page-header-logout-icon" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageHeader;
